import { useState } from 'react'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import { useCategories } from '../../../features/products/hooks/useMetaData'
import { metaDataApi } from '../../../features/products/api/metaDataApi'
import { MetaTable } from '../components/MetaTable'
import { Input } from '../../../shared/components'
import Button from '../../../shared/components/Button'

export const CategoriesTab = () => {
  const { data, isLoading } = useCategories()
  const queryClient = useQueryClient()
  const [editingId, setEditingId] = useState<string | null>(null)

  const createMutation = useMutation({
    mutationFn: (name: string) => metaDataApi.createCategory(name),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['categories'] }),
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => { await metaDataApi.updateCategory(id, name) },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['categories'] }); setEditingId(null) },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => metaDataApi.deleteCategory(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['categories'] }),
  })

  const items = (data?.data ?? []) as unknown as Record<string, unknown>[]

  const columns = [{ key: 'name', label: 'Name' }]

  const renderAddForm = (onDone: () => void) => {
    return (
      <AddForm
        placeholder="Category name"
        isLoading={createMutation.isPending}
        onSubmit={async (val) => { await createMutation.mutateAsync(val); onDone() }}
        onCancel={onDone}
      />
    )
  }

  const renderEditForm = (item: Record<string, unknown>, onDone: () => void) => (
    <EditForm
      defaultValue={item.name as string}
      isLoading={updateMutation.isPending}
      onSubmit={async (val) => updateMutation.mutateAsync({ id: item.id as string, name: val })}
      onCancel={onDone}
    />
  )

  return (
    <MetaTable
      title="Categories"
      items={items}
      columns={columns}
      isLoading={isLoading}
      addLabel="Add Category"
      renderAddForm={renderAddForm}
      onEdit={(item) => setEditingId(editingId === (item.id as string) ? null : (item.id as string))}
      onDelete={(item) => deleteMutation.mutate(item.id as string)}
      editingId={editingId}
      renderEditForm={renderEditForm}
    />
  )
}

const AddForm = ({ placeholder, isLoading, onSubmit, onCancel }: {
  placeholder: string
  isLoading: boolean
  onSubmit: (val: string) => Promise<void>
  onCancel: () => void
}) => {
  const [value, setValue] = useState('')
  return (
    <div className="flex items-end gap-3">
      <div className="flex-1">
        <Input label="Name" placeholder={placeholder} value={value} onChange={e => setValue(e.target.value)} className="h-[38px]" />
      </div>
      <Button size="sm" onClick={() => onSubmit(value)} disabled={!value.trim() || isLoading} loading={isLoading} className="h-[38px]">Save</Button>
      <Button size="sm" variant="outline" onClick={onCancel} className="h-[38px]">Cancel</Button>
    </div>
  )
}

const EditForm = ({ defaultValue, isLoading, onSubmit, onCancel }: {
  defaultValue: string
  isLoading: boolean
  onSubmit: (val: string) => Promise<void>
  onCancel: () => void
}) => {
  const [value, setValue] = useState(defaultValue)
  return (
    <div className="flex items-end gap-3">
      <div className="flex-1">
        <Input label="Name" value={value} onChange={e => setValue(e.target.value)} className="h-[38px]" />
      </div>
      <Button size="sm" onClick={() => onSubmit(value)} disabled={!value.trim() || isLoading} loading={isLoading} className="h-[38px]">Update</Button>
      <Button size="sm" variant="outline" onClick={onCancel} className="h-[38px]">Cancel</Button>
    </div>
  )
}
